import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import { groupAPI, learningAPI } from '../../services/api';

const formatDate = (value) => {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity yet';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatScore = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value) * 10) / 10}%` : '—');

function CohortDetailPage() {
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [studentDirectory, setStudentDirectory] = useState({ total: 0, truncated: false });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadCohort = useCallback(async () => {
    setLoading(true);
    try {
      const [groupResponse, studentsResponse] = await Promise.all([
        learningAPI.getGroup(groupId),
        learningAPI.listStudents(),
      ]);
      const nextGroup = groupResponse.data?.group || null;
      const nextStudents = studentsResponse.data?.students || [];
      setGroup(nextGroup);
      setAllStudents(nextStudents);
      setStudentDirectory({
        total: studentsResponse.data?.total || nextStudents.length,
        truncated: Boolean(studentsResponse.data?.truncated),
      });
      const memberIds = new Set((nextGroup?.members || []).map((member) => member.id));
      setSelectedStudents(nextStudents.filter((student) => memberIds.has(student.id)));
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load this cohort.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    loadCohort();
  }, [loadCohort]);

  const saveMembership = async () => {
    setSaving(true);
    try {
      const existingIds = new Set((group?.members || []).map((member) => member.id));
      const loadedIds = new Set(allStudents.map((student) => student.id));
      const selectedIds = new Set(selectedStudents.map((student) => student.id));
      const addMemberIds = Array.from(selectedIds).filter((id) => !existingIds.has(id));
      const removeMemberIds = Array.from(existingIds)
        .filter((id) => loadedIds.has(id) && !selectedIds.has(id));
      await groupAPI.assignMembers(groupId, { addMemberIds, removeMemberIds });
      setMembershipOpen(false);
      await loadCohort();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to update cohort membership.');
    } finally {
      setSaving(false);
    }
  };

  const memberIds = useMemo(() => new Set((group?.members || []).map((member) => member.id)), [group]);

  if (loading) {
    return <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }

  if (!group) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error" action={<Button component={RouterLink} to="/university/teaching?tab=cohorts">Back to cohorts</Button>}>
          {error || 'Cohort not found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Button component={RouterLink} to="/university/teaching?tab=cohorts" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 2 }}>
        Teaching workspace
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 900 }}>{group.name}</Typography>
            {group.cohortCode && <Chip label={group.cohortCode} color="primary" />}
            {group.academicYear && <Chip label={group.academicYear} variant="outlined" />}
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>{group.description || 'No description has been added.'}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" component={RouterLink} to="/university/teaching?tab=assignments">Assign a case</Button>
          <Button variant="contained" startIcon={<GroupAddRoundedIcon />} onClick={() => setMembershipOpen(true)}>Manage learners</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Learners', group.summary?.memberCount || 0],
          ['Completed attempts', group.summary?.attempts || 0],
          ['Average score', formatScore(group.summary?.averageScore)],
          ['Active assignments', group.summary?.activeAssignments || 0],
        ].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>{label}</Typography>
                <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900 }}>{value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Learners</Typography>
          <Typography variant="body2" color="text.secondary">Open a learner profile for detailed performance evidence and completed cases.</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Learner</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell align="right">Average</TableCell>
                <TableCell>Last attempt</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(group.members || []).map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{member.name || 'Unnamed learner'}</Typography>
                    <Typography variant="caption" color="text.secondary">{member.email}</Typography>
                  </TableCell>
                  <TableCell align="right">{member.performance?.attempts || 0}</TableCell>
                  <TableCell align="right">{formatScore(member.performance?.averageScore)}</TableCell>
                  <TableCell>{formatDate(member.performance?.lastAttemptAt)}</TableCell>
                  <TableCell align="right">
                    <Button component={RouterLink} to={`/university/teaching/students/${member.id}`} endIcon={<ArrowForwardRoundedIcon />}>View insights</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!group.members?.length && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}>No learners are assigned to this cohort.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={membershipOpen} onClose={() => setMembershipOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Manage {group.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add or remove active learners. Removing a learner does not delete their account or history.
          </Typography>
          {studentDirectory.truncated && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing {allStudents.length} of {studentDirectory.total} active learners. Existing learners outside this list are preserved.
            </Alert>
          )}
          <Autocomplete
            multiple
            options={allStudents}
            value={selectedStudents}
            onChange={(_event, nextStudents) => setSelectedStudents(nextStudents)}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Learners" placeholder="Search by name or email" />}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {selectedStudents.length} selected · {allStudents.filter((student) => !memberIds.has(student.id)).length} other active learners available
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembershipOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveMembership} disabled={saving}>{saving ? 'Saving…' : 'Save membership'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CohortDetailPage;
